import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../framework/redux/hooks";
import { useTranslation } from "react-i18next";
import appConfig from "../../application/appConfig";
import { useNavigate } from "react-router-dom";
import { handleLogin } from "../../framework/redux/slices/sessionSlice";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import { refreshLogin } from "../../services/tokenService";
import { LoadingButton } from "../framework/loadingButton";
import Grid2 from "@mui/material/Unstable_Grid2";

export interface ILoginPageState {
    username: string;
    password: string;
}

export const LoginPage = () => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const { t } = useTranslation();
    const handleTogglePasswordVisibility = useCallback(() => setShowPassword((prev) => !prev), []);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { accessToken } = useAppSelector((state) => state.session);
    const [state, setState] = useState<ILoginPageState>({
        username: "",
        password: "",
    });

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (state.username && state.password) {
            setSubmitting(true);
            await dispatch(handleLogin(state.username, state.password));
            setSubmitting(false);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "username") {
            setState({ ...state, username: value });
        } else if (name === "password") {
            setState({ ...state, password: value });
        }
    };

    const refreshSession = () => {
        refreshLogin()
            .then(() => navigate("/"))
            .catch(console.error);
    };

    useEffect(() => {
        if (accessToken) {
            refreshSession();
        }
    }, [accessToken]);

    return (
        <Grid2 container>
            <Grid2
                xs={12}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 7,
                    px: 5,
                    backgroundImage: "url(" + appConfig.clientUrl + "ImageLogin-EasyOpp.jpg)",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: (t) =>
                        t.palette.mode === "light"
                            ? t.palette.grey[50]
                            : t.palette.grey[900],
                    backgroundSize: "cover",
                    backgroundPosition: "top center",
                }}
            >
                <img
                    src={appConfig.clientUrl + "EasyOpp_vertical.svg"}
                    alt="EasyOpp - Decorative logo"
                    style={{ width: "100%", maxWidth: 400 }}
                />
            </Grid2>

            <Grid2
                component="form"
                container
                gap={2}
                p={3}
                xs={12}
                noValidate
                onSubmit={(e) => void handleSubmit(e)}
            >
                <Grid2 xs={12}>
                    <TextField
                        required
                        fullWidth
                        id="username"
                        label={t("login.username")}
                        name="username"
                        autoComplete="username"
                        autoFocus
                        onChange={handleChange}
                    />
                </Grid2>
                <Grid2 xs={12}>
                    <TextField
                        required
                        fullWidth
                        name="password"
                        label={t("login.password")}
                        type={showPassword ? "text" : "password"}
                        id="password"
                        autoComplete="current-password"
                        onChange={handleChange}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility"
                                        aria-label={t("login.togglePasswordVisibility")}
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid2>
                <Grid2 xs={12}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={submitting}
                        startIcon={<LoginIcon />}
                    >
                        {t("login.loginButton")}
                    </LoadingButton>
                </Grid2>
                {/*TODO implement password recovery*/}
                {/*<Grid2 xs={12}>*/}
                {/*    <Link href="#" variant="body2">*/}
                {/*        {t("login.forgotPassword")}*/}
                {/*    </Link>*/}
                {/*</Grid2>*/}
            </Grid2>
        </Grid2>
    );
};
